/**
 * New Year kids' crafts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearKidsCraftsPack: ToilePack = {
    slug: 'new-year-kids-crafts',
    category: 'season',
    name: "New Year kids' crafts",
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'clock', label: 'Paper plate clock', subject: 'a craft template lettered "12" at the top with clock numbers and two hands to cut and pin on a plate, dashed cut lines'},
        {key: 'handprint', label: 'Handprint firework', subject: 'a craft sheet lettered "BOOM!" above a night sky with space for handprints as firework bursts, a dotted guide'},
        {key: 'hat', label: 'Cut and colour hat', subject: 'a craft sheet lettered "2026" on a party hat shape with patterns to colour and cut, dashed cut lines'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a vertical page with a gold border and an even margin, the craft centred, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin, the craft centred, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a vertical page with wide margins, the craft centred, print-ready.'},
    ],
    directions: ['for a classroom of toddlers', 'with a snowman instead', 'sized for a bulletin board'],
    directionHint: 'try: with a snowman instead',
    seo: {
        title: "New Year Kids' Crafts: Clock, Firework & Hat",
        metaDescription: 'Nine New Year craft sheets for kids — a paper plate clock, a handprint firework, and a cut-and-colour hat — picture-only steps, print-ready.',
        h1: "New Year kids' crafts: clock, firework, and hat templates",
        tagline: 'Nine sheets, three crafts, three styles each — steps shown as pictures, not words.',
        intro: 'Three New Year craft sheets for the classroom: a paper-plate clock, a handprint firework, and a cut-and-colour party hat, each with every step drawn as a picture, in black-and-gold, confetti-party, and minimal styles. Ask for a different craft or an older-kid version, and the same craft grammar carries it.',
        useCases: [
            'a preschool or kindergarten countdown party',
            "a family New Year's Eve activity for kids",
            'a classroom January craft session',
            'a rainy-day activity at home',
        ],
        faq: [
            {q: 'Are the instructions written out, or shown as pictures?', a: 'Shown as pictures — the steps are drawn, not written, so pre-readers can follow along.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a different craft or an older-kid version?', a: 'Yes — the direction field takes any variation, including a different craft or an older-kid layout.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
